import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

export default function AcceptInvite({ onComplete }) {
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [inviteData, setInviteData] = useState(null)
  const [step, setStep] = useState('loading') // loading | form | done

  useEffect(() => {
    async function processInvite() {
      // Get the session from the invite link
      const { data, error } = await supabase.auth.getSession()
      if (error || !data.session) {
        setError('Invalid or expired invite link. Please ask your admin to resend.')
        setStep('form')
        return
      }

      const user = data.session.user
      const metadata = user.user_metadata || {}

      setInviteData({
        email: user.email,
        clubName: metadata.club_name || 'your club',
        clubId: metadata.club_id,
        squadId: metadata.squad_id,
        role: metadata.role || 'coach',
        invitedByName: metadata.invited_by_name || 'your admin'
      })
      setStep('form')
    }

    processInvite()
  }, [])

  async function handleAccept() {
    if (!fullName.trim()) { setError('Please enter your full name.'); return }
    if (!password || password.length < 6) { setError('Password must be at least 6 characters.'); return }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return }
    setLoading(true); setError('')

    try {
      // Update password
      const { error: pwError } = await supabase.auth.updateUser({ password })
      if (pwError) { setError('Error setting password: ' + pwError.message); setLoading(false); return }

      const { data: { session } } = await supabase.auth.getSession()
      const userId = session?.user?.id
      if (!userId) { setError('Session error. Please try again.'); setLoading(false); return }

      // Check for pending invite
      const { data: pendingInvite } = await supabase.from('pending_invites')
        .select('*').eq('email', session.user.email).single()

      const clubId = inviteData?.clubId || pendingInvite?.club_id
      const squadId = inviteData?.squadId || pendingInvite?.squad_id
      const role = inviteData?.role || pendingInvite?.role || 'coach'

      // Create profile
      const { error: profileError } = await supabase.from('profiles').upsert({
        id: userId,
        full_name: fullName.trim(),
        club_id: clubId,
        club_name: inviteData?.clubName,
        role: role,
        approved: true
      })
      if (profileError) { setError('Error creating profile: ' + profileError.message); setLoading(false); return }

      // Assign to squad
      if (squadId) {
        await supabase.from('squad_members').upsert({
          squad_id: squadId,
          profile_id: userId,
          role: role,
          approved: true
        })
      }

      // Clean up pending invite
      if (pendingInvite) {
        await supabase.from('pending_invites').delete().eq('id', pendingInvite.id)
      }

      setStep('done')
      setTimeout(() => onComplete(), 2000)
    } catch (e) {
      setError('Error: ' + e.message)
    }
    setLoading(false)
  }

  if (step === 'loading') {
    return (
      <div style={shell}>
        <div style={card}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
            <p style={{ color: '#888' }}>Processing your invite...</p>
          </div>
        </div>
      </div>
    )
  }

  if (step === 'done') {
    return (
      <div style={shell}>
        <div style={card}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>Welcome!</h2>
            <p style={{ color: '#888', fontSize: '14px' }}>Your account is ready. Taking you to the app...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={shell}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>🏐</div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#185FA5' }}>GAA Coach Tracker</h1>
          {inviteData && (
            <p style={{ fontSize: '13px', color: '#888', marginTop: '6px', lineHeight: '1.5' }}>
              You've been invited to join <strong>{inviteData.clubName}</strong> by {inviteData.invitedByName}
            </p>
          )}
        </div>

        {error && (
          <div style={{ background: '#FCEBEB', border: '0.5px solid #A32D2D', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '13px', color: '#A32D2D' }}>
            {error}
          </div>
        )}

        <p style={{ fontSize: '13px', color: '#555', marginBottom: '16px', fontWeight: '500' }}>
          Set up your account
        </p>

        <input style={inputStyle} type="text" placeholder="Your full name *"
          value={fullName} onChange={e => setFullName(e.target.value)} />
        <input style={inputStyle} type="password" placeholder="Create a password *"
          value={password} onChange={e => setPassword(e.target.value)} />
        <input style={inputStyle} type="password" placeholder="Confirm password *"
          value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />

        {inviteData && (
          <div style={{ background: '#f8f8f8', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '12px', color: '#888' }}>
            <p>📧 {inviteData.email}</p>
            <p>🏐 {inviteData.clubName}</p>
            <p>👤 Role: {inviteData.role === 'squad_admin' ? 'Squad Admin' : inviteData.role === 'club_admin' ? 'Club Admin' : 'Coach'}</p>
          </div>
        )}

        <button style={{ ...primaryBtn, opacity: loading ? 0.7 : 1 }}
          onClick={handleAccept} disabled={loading}>
          {loading ? 'Setting up account...' : '✅ Accept Invite & Join'}
        </button>
      </div>
    </div>
  )
}

const shell = { minHeight: '100vh', background: 'linear-gradient(135deg, #185FA5 0%, #0d3d6b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }
const card = { background: 'white', borderRadius: '16px', padding: '28px 24px', width: '100%', maxWidth: '380px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }
const inputStyle = { display: 'block', width: '100%', padding: '12px 14px', marginBottom: '12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }
const primaryBtn = { display: 'block', width: '100%', padding: '13px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '500', cursor: 'pointer' }